import { useState, useCallback, useRef, useEffect } from 'react';
import useStateStorage from '../shared/storage/stateStorage';

const STORAGE_KEY = 'llmchat-sidebar-width';
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 450;

function clamp(value: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));
}

export default function useSidebarWidth() {
  const storage = useStateStorage<number>(STORAGE_KEY);
  const [width, setWidth] = useState(() => clamp(storage.getValue(DEFAULT_WIDTH)));
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const widthRef = useRef(width);
  widthRef.current = width;
  const listenersRef = useRef<{ move: (e: MouseEvent) => void; up: (e: MouseEvent) => void } | null>(null);

  useEffect(() => {
    return () => {
      if (listenersRef.current) {
        document.removeEventListener('mousemove', listenersRef.current.move);
        document.removeEventListener('mouseup', listenersRef.current.up);
      }
    };
  }, []);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = widthRef.current;
    setIsDragging(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = clamp(startWidthRef.current + (moveEvent.clientX - startXRef.current));
      setWidth(newWidth);
    };

    const onMouseUp = (upEvent: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      listenersRef.current = null;
      const finalWidth = clamp(startWidthRef.current + (upEvent.clientX - startXRef.current));
      setWidth(finalWidth);
      storage.setValue(finalWidth);
      setIsDragging(false);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    listenersRef.current = { move: onMouseMove, up: onMouseUp };
  }, [storage]);

  return { width, isDragging, onDragStart };
}
